import {
  CategoryEntity,
  categoryIdToCategoryValue,
  DocumentType,
  EntityRefUtil,
  formatDateAsUTC,
  formatDateWithUserTimezone,
  GetDocumentsByAssetRequestDTO,
  GetDocumentsByAssetResponseDTO,
} from '@gearbelt/global';
import { AccessorKeyColumnDef, createColumnHelper } from '@tanstack/react-table';
import { startCase } from 'lodash';

import { Checkbox } from '@/components/ui/checkbox';
import { UserAvatarLabel } from '@/components/users/user-avatar-label';
import { initials } from '@/lib/utils';
import { ApiService } from '@/services/api-v2';
import { useUserStore } from '@/stores/user.store';
import { getAvatarBackgroundColorClass } from '@/utils/avatar';
import { getFileTypeIcon } from '@/utils/file-type';

const columnHelper = createColumnHelper<GetDocumentsByAssetResponseDTO['data'][number]>();

export const columns = [
  columnHelper.display({
    id: 'select',
    header: ({ table }) => (
      <div className="flex w-full flex-col items-center justify-center">
        <label className="-m-2 grid place-items-center p-2" onClick={(e) => e.stopPropagation()}>
          <Checkbox checked={table.getIsAllPageRowsSelected()} onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)} aria-label="Select all" />
        </label>
      </div>
    ),
    cell: ({ row }) => (
      <div className="flex w-full flex-col items-center justify-center">
        <label className="-m-2 grid place-items-center p-2" onClick={(e) => e.stopPropagation()}>
          <Checkbox checked={row.getIsSelected()} onCheckedChange={(value) => row.toggleSelected(!!value)} aria-label="Select row" />
        </label>
      </div>
    ),
    meta: {
      cellClassName: 'w-[40px] min-w-[40px] max-w-[40px]',
    },
    enableSorting: false,
    enableHiding: false,
  }),
  columnHelper.accessor('name', {
    id: 'name',
    header: 'Document',
    cell: ({ getValue, row }) => {
      const name = getValue();
      if (!name) return <span className="text-secondary-text">-</span>;

      return (
        <a
          className="text-default-blue flex w-full items-center gap-1 underline"
          href={row.original.presignedDownloadUrl || row.original.url}
          target="_blank"
          rel="noopener noreferrer">
          {getFileTypeIcon(row.original.mimeType ?? '')}
          <span className="truncate">{name}</span>
        </a>
      );
    },
    meta: {
      cellClassName: 'max-w-[300px]',
    },
  }),
  columnHelper.accessor('type', {
    id: 'type',
    header: 'Document Category',
    cell: ({ getValue, row }) => {
      const value = getValue();
      const category = row.original.category;

      return <span>{category ? category.name : startCase(value !== null ? DocumentType[value] : '')}</span>;
    },
    enableColumnFilter: true,
    meta: {
      filterType: 'multi-select',
      // Document category is always set (type or categoryId) — "-" filter is not applicable.
      disableNullFilter: true,
      getFilterOptionsFn: async () => {
        // Every DocumentType value (the 4 system types and the 6 legacy
        // default ones) now always has a permanent real CategoryTable row,
        // so the live category list alone covers every filterable option —
        // listing raw types here too just shows every legacy name twice.
        const { data: categories } = await (await ApiService.GetCategories({ filters: { fields: { entity: CategoryEntity.Document } } })).json();

        return categories.map((category) => ({
          label: category.name,
          value: categoryIdToCategoryValue(category.id),
        }));
      },
    },
  }),
  columnHelper.accessor('expiry', {
    id: 'expiry',
    header: 'Expiry Date',
    cell: ({ getValue }) => {
      const value = getValue();
      const userTimezone = useUserStore.getState().user?.timezone;

      return value ? <span>{formatDateAsUTC(new Date(value), userTimezone)}</span> : <span className="text-secondary-text">-</span>;
    },
  }),
  columnHelper.accessor('createdAt', {
    id: 'createdAt',
    header: 'Created Date',
    cell: ({ getValue }) => {
      const value = getValue();
      const userTimezone = useUserStore.getState().user?.timezone;

      return value ? <span>{formatDateWithUserTimezone(new Date(value), userTimezone)}</span> : <span className="text-secondary-text">-</span>;
    },
  }),
  columnHelper.accessor('asset', {
    id: 'asset',
    header: 'Asset',
    cell: ({ getValue }) => {
      const value = getValue();

      return value?.id ? <span className="truncate">{EntityRefUtil.createAssetLabel(value)}</span> : <span className="text-secondary-text">-</span>;
    },
    enableSorting: false,
  }),
  columnHelper.accessor((row) => row.user, {
    id: 'createdBy',
    header: 'Created By',
    cell: ({ row }) => {
      const document = row.original;
      const createdBy = document.user;
      const fullName = EntityRefUtil.createUserFullNameRef(createdBy);
      const value = fullName || '-';
      const avatarBackgroundColorClass = getAvatarBackgroundColorClass(createdBy?.firstName || '');

      const createdByFullInitials = initials(fullName);
      return (
        <UserAvatarLabel
          value={value}
          operatedByFullInitials={createdByFullInitials}
          avatarUrl={createdBy?.avatarUrl || ''}
          avatarBackgroundColorClass={avatarBackgroundColorClass}
        />
      );
    },
    enableGrouping: false,
    enableColumnFilter: false,
  }),
] as AccessorKeyColumnDef<GetDocumentsByAssetResponseDTO['data'][number]>[];

export const FILTER_FIELD_MAP: Record<string, keyof NonNullable<NonNullable<GetDocumentsByAssetRequestDTO['filters']>['fields']>> = {
  type: 'type',
};
